/**
 * Pre-Spend Gate — single source of truth for blocking actions that
 * cost the company money before the deal is contractually safe.
 *
 * THREE GATES (all must clear):
 *   1. Project Review — build/spec validated by Ops (intakeApproved + approvedForOrdering)
 *   2. Rescission     — legal cooling-off period expired
 *   3. Financial      — deposit / financing cleared (pre_schedule stage)
 *
 * GATED ACTIONS (anything that obligates company money or inventory):
 *   • Order materials / Generate PO
 *   • Submit permit application
 *   • Allocate / pull stock from warehouse
 *   • Schedule install / Assign crew
 *
 * NOT GATED (safe before clearance):
 *   • Customer communication
 *   • Notes, document uploads
 *   • Viewing / read-only access
 *   • Project Review itself (it IS the gate)
 *
 * USAGE:
 *   const gate = derivePreSpendGate(job);
 *   if (!gate.cleared) { showBanner(gate); disableButton(); }
 *
 * Pure function — no side effects, fully testable, deterministic.
 */

import type { Job } from "./types";
import { isInRescission, getRescissionDaysRemaining, formatRescissionEnd } from "./rescission";
import { deriveFinancialClearance } from "./financialClearance";
import { isExternalManagedImport, migrationBypasses } from "./import/migrationVisibilityGuard";

export type PreSpendGateName = "review" | "rescission" | "financial";

export interface PreSpendGateCheck {
  name: PreSpendGateName;
  label: string;
  cleared: boolean;
  /** Operator-friendly status line, e.g. "Pending Ops review" or "Ends Mar 20 (2 business days)" */
  detail: string;
}

export interface PreSpendGate {
  /** True only when ALL three sub-gates are cleared. */
  cleared: boolean;
  checks: PreSpendGateCheck[];
  /** First unmet gate — useful for headline/toast messaging. */
  primaryBlocker?: PreSpendGateCheck;
  /** One-line summary suitable for badges/buttons. */
  shortReason: string;
}

/** Project Review gate — uses Job's intake/ordering approval flags set by BuildReviewPanel. */
function checkProjectReview(job: Job): PreSpendGateCheck {
  // approvedForOrdering is set when Ops marks the build review approved.
  // intakeApproved alone (without approvedForOrdering) means review started but isn't final.
  const cleared = !!job.approvedForOrdering;
  return {
    name: "review",
    label: "Project Review",
    cleared,
    detail: cleared
      ? "Approved by Ops"
      : job.intakeApproved
        ? "In review — awaiting final approval"
        : "Not yet submitted to Ops",
  };
}

/** Rescission gate — 3-business-day legal cooling-off period. */
function checkRescission(job: Job): PreSpendGateCheck {
  const inRescission = isInRescission(job.rescissionEndsAt);
  if (!job.rescissionEndsAt) {
    // No tracked rescission window — treat as cleared (legacy / non-applicable deals).
    return { name: "rescission", label: "Rescission", cleared: true, detail: "Not applicable" };
  }
  if (!inRescission) {
    return { name: "rescission", label: "Rescission", cleared: true, detail: "Cleared for production" };
  }
  const daysLeft = getRescissionDaysRemaining(job.rescissionEndsAt);
  return {
    name: "rescission",
    label: "Rescission",
    cleared: false,
    detail: `Ends ${formatRescissionEnd(job.rescissionEndsAt)} (${daysLeft} business day${daysLeft === 1 ? "" : "s"} left)`,
  };
}

/** Financial gate — deposit / financing must be cleared at the pre_schedule stage. */
function checkFinancial(job: Job): PreSpendGateCheck {
  const fc = deriveFinancialClearance(job, "pre_schedule");
  return {
    name: "financial",
    label: "Financial Clearance",
    cleared: fc.status === "cleared",
    detail: fc.status === "cleared" ? "Deposit / financing cleared" : fc.reason,
  };
}

/**
 * Derive the combined pre-spend gate for a job.
 * Returns `cleared: true` only when all three sub-gates pass.
 */
export function derivePreSpendGate(job: Job): PreSpendGate {
  if (isExternalManagedImport(job)) {
    const buildReviewBypassed = migrationBypasses(job, "build_review");
    const checks: PreSpendGateCheck[] = [
      buildReviewBypassed
        ? { name: "review", label: "Project Review", cleared: true, detail: "Bypassed (externally managed import)" }
        : checkProjectReview(job),
      { name: "rescission", label: "Rescission", cleared: true, detail: "Bypassed (externally managed import)" },
      { name: "financial", label: "Financial Clearance", cleared: true, detail: "Bypassed (externally managed import)" },
    ];
    const blockers = checks.filter((c) => !c.cleared);
    const cleared = blockers.length === 0;
    const primaryBlocker = blockers[0];
    let shortReason: string;
    if (cleared) {
      shortReason = "All gates cleared — safe to spend";
    } else if (blockers.length === 1) {
      shortReason = `${primaryBlocker!.label} pending`;
    } else {
      shortReason = `${blockers.length} gates pending: ${blockers.map((b) => b.label).join(" · ")}`;
    }
    return { cleared, checks, primaryBlocker, shortReason };
  }

  const checks: PreSpendGateCheck[] = [
    checkProjectReview(job),
    checkRescission(job),
    checkFinancial(job),
  ];
  const blockers = checks.filter((c) => !c.cleared);
  const cleared = blockers.length === 0;
  const primaryBlocker = blockers[0];

  let shortReason: string;
  if (cleared) {
    shortReason = "All gates cleared — safe to spend";
  } else if (blockers.length === 1) {
    shortReason = `${primaryBlocker!.label} pending`;
  } else {
    shortReason = `${blockers.length} gates pending: ${blockers.map((b) => b.label).join(" · ")}`;
  }

  return { cleared, checks, primaryBlocker, shortReason };
}

/**
 * Convenience: the headline shown on a blocked CTA.
 * "Order materials" → "Awaiting project review" / "In rescission until Mar 20" / etc.
 */
export function getGatedCtaLabel(action: string, gate: PreSpendGate): string {
  if (gate.cleared) return action;
  if (!gate.primaryBlocker) return action;
  switch (gate.primaryBlocker.name) {
    case "review":     return `${action} — review pending`;
    case "rescission": return `${action} — in rescission`;
    case "financial":  return `${action} — deposit pending`;
  }
}
